import type { CalendarWorkout } from '../services/api';

/** Calendar-day arithmetic uses UTC dates so DST does not shift multi-day plans. */
function dayNumber(iso: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) throw new Error('Invalid calendar date');
  const [, year, month, day] = match.map(Number);
  const value = Date.UTC(year, month - 1, day);
  if (new Date(value).toISOString().slice(0, 10) !== iso) throw new Error('Invalid calendar date');
  return value / 86400000;
}

export function shiftCalendarDate(iso: string, offset: number): string {
  return new Date((dayNumber(iso) + offset) * 86400000).toISOString().slice(0, 10);
}

export interface CalendarCopyTask {
  key: string;
  athleteId: number;
  workoutId: number;
  sourceDate: string;
  destination: string;
}

export type CalendarCopyResult = 'success' | 'failed' | 'unknown';

/** Serial requests avoid flooding the API. Ambiguous responses must never be retried automatically. */
export async function executeCalendarCopyPlan(
  tasks: CalendarCopyTask[],
  previous: Record<string, CalendarCopyResult>,
  send: (task: CalendarCopyTask) => Promise<unknown>,
  settled: (key: string, result: CalendarCopyResult) => void,
): Promise<number> {
  let successes = 0;
  for (const task of tasks) {
    if (previous[task.key] === 'success' || previous[task.key] === 'unknown') continue;
    let result: CalendarCopyResult;
    try {
      await send(task);
      result = 'success'; successes++;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      result = status && [400, 401, 403, 404, 405, 422, 429].includes(status) ? 'failed' : 'unknown';
    }
    settled(task.key, result);
  }
  return successes;
}

export function buildCalendarCopyPlan(
  workouts: Pick<CalendarWorkout, 'id' | 'workout_day'>[],
  athleteIds: number[],
  startDate: string | null,
): CalendarCopyTask[] {
  const unique = [...new Map(workouts.map(w => [w.id, w])).values()];
  if (!unique.length) return [];
  const first = Math.min(...unique.map(w => dayNumber(w.workout_day.slice(0, 10))));
  const offset = startDate == null ? 0 : dayNumber(startDate) - first;
  return [...new Set(athleteIds)].flatMap(athleteId => unique.map(workout => {
    const sourceDate = workout.workout_day.slice(0, 10);
    const destination = shiftCalendarDate(sourceDate, offset);
    return { key: `${athleteId}:${workout.id}:${destination}`, athleteId, workoutId: workout.id, sourceDate, destination };
  }));
}
